import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, Lock, Mail, CheckCircle2, XCircle, ArrowRight, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import Logo from '@/components/Logo';
import SEO from '@/components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  // 'request'  → user has no token, show "enter email" form (Forgot Password)
  // 'sent'     → reset email dispatched
  // 'form'     → user has token, show "create new password" form
  // 'success'  → password reset complete
  // 'error'    → token invalid / expired
  const [status, setStatus] = useState(() => (searchParams.get('token') ? 'form' : 'request'));
  const [passwords, setPasswords] = useState({
    password: '',
    confirmPassword: ''
  });
  const [forgotEmail, setForgotEmail] = useState('');
  const navigate = useNavigate();

  const token = searchParams.get('token');

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) {
      toast.error('Please enter your email');
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email: forgotEmail.trim().toLowerCase() });
      setStatus('sent');
    } catch (error) {
      // Treat as success regardless to avoid leaking which emails are registered.
      setStatus('sent');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (passwords.password !== passwords.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (passwords.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setIsLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, {
        token: token,
        new_password: passwords.password
      });
      
      setStatus('success');
      toast.success('Password reset successfully!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to reset password';
      toast.error(message);
      if (message.includes('expired') || message.includes('Invalid')) {
        setStatus('error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <SEO title="Reset Password" description="Reset your Poettik account password." noindex />
      {/* Animated background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/10 rounded-full blur-3xl animate-pulse-glow" />
        <div className="absolute bottom-20 -left-40 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
      </div>

      <Card variant="glass" className="relative z-10 max-w-md w-full p-6">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 justify-center mb-6">
          <Logo size="lg" />
        </Link>

        {/* Request State — user clicked "Forgot password?" with no token */}
        {status === 'request' && (
          <>
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 rounded-full bg-primary/15 flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-primary" />
              </div>
              <CardTitle className="font-display text-2xl">Forgot Password?</CardTitle>
              <CardDescription>
                Enter your email and we&apos;ll send you a link to reset it.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="forgot-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="forgot-email"
                      type="email"
                      placeholder="you@example.com"
                      className="pl-10"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  variant="premium"
                  size="lg"
                  className="w-full mt-2"
                  disabled={isLoading}
                  data-testid="forgot-submit-btn"
                >
                  {isLoading ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Sending…</>
                  ) : (
                    <>Send reset link<ArrowRight className="w-5 h-5 ml-2" /></>
                  )}
                </Button>

                <Link to="/login" className="block">
                  <Button type="button" variant="ghost" size="sm" className="w-full text-muted-foreground">
                    <ArrowLeft className="w-4 h-4 mr-1.5" />
                    Back to sign in
                  </Button>
                </Link>
              </form>
            </CardContent>
          </>
        )}

        {/* Sent State — reset email dispatched */}
        {status === 'sent' && (
          <>
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-primary" />
              </div>
              <CardTitle className="font-display text-2xl">Check Your Email</CardTitle>
              <CardDescription>
                If an account exists for <strong>{forgotEmail}</strong>, you&apos;ll receive an email with instructions to reset your password within a few minutes.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4 space-y-2">
              <Link to="/login">
                <Button variant="premium" size="lg" className="w-full" data-testid="forgot-back-to-login">
                  Back to Sign In
                </Button>
              </Link>
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-muted-foreground"
                onClick={() => { setStatus('request'); setForgotEmail(''); }}
                data-testid="forgot-try-different-email"
              >
                Try a different email
              </Button>
            </CardContent>
          </>
        )}

        {/* Error State - No token */}
        {status === 'error' && (
          <>
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 rounded-full bg-destructive/20 flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-destructive" />
              </div>
              <CardTitle className="font-display text-2xl">Invalid Reset Link</CardTitle>
              <CardDescription>
                This password reset link is invalid or has expired. Please request a new one.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <Link to="/login">
                <Button variant="premium" size="lg" className="w-full">
                  Back to Sign In
                </Button>
              </Link>
            </CardContent>
          </>
        )}

        {/* Success State */}
        {status === 'success' && (
          <>
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-primary" />
              </div>
              <CardTitle className="font-display text-2xl">Password Reset!</CardTitle>
              <CardDescription>
                Your password has been successfully reset. You can now sign in with your new password.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <Link to="/login">
                <Button variant="premium" size="lg" className="w-full">
                  Sign In Now
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </>
        )}

        {/* Form State */}
        {status === 'form' && (
          <>
            <CardHeader className="text-center pb-2">
              <CardTitle className="font-display text-2xl">Create New Password</CardTitle>
              <CardDescription>
                Enter your new password below
              </CardDescription>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password">New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      className="pl-10"
                      value={passwords.password}
                      onChange={(e) => setPasswords(prev => ({ ...prev, password: e.target.value }))}
                      required
                      minLength={6}
                      data-testid="new-password-input"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Minimum 6 characters</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      className="pl-10"
                      value={passwords.confirmPassword}
                      onChange={(e) => setPasswords(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      required
                      minLength={6}
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  variant="premium"
                  size="lg"
                  className="w-full mt-6"
                  disabled={isLoading}
                  data-testid="reset-password-btn"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Resetting...
                    </>
                  ) : (
                    <>
                      Reset Password
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
